/**
 * The calendar (`/calendar`).
 *
 * Week is the default view because a week is the unit a sales rep plans in;
 * day exists for a full one, and the agenda list for scanning what is next
 * without reading a grid.
 *
 * Shortcuts are registered in the central registry like everything else
 * (CLAUDE.md §12): `t` today, `[` / `]` previous and next, `n` a new activity.
 * `[` and `]` are the physical keys either side of Enter on both a Persian and
 * a Latin layout, so they land in the same place whichever the user has.
 */
import { useMutation, useQuery } from '@apollo/client';
import { activityPalette } from '@meshhub/design-tokens/tokens';
import { Button, Spinner } from '@meshhub/design-tokens/ui';
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityChip } from '../components/calendar/ActivityChip.web.jsx';
import { ActivityDialog } from '../components/calendar/ActivityDialog.web.jsx';
import { WeekGrid } from '../components/calendar/WeekGrid.web.jsx';
import { JalaliDateButton, MutationError } from '../components/FormKit.web.jsx';
import {
  addDays,
  formatDayHeader,
  formatGregorian,
  formatWeekRange,
  isoToLocalDate,
  persianWeekDays,
  startOfDay,
  toIsoDate,
} from '../lib/format.js';
import {
  ACTIVITY_TYPES_QUERY,
  CALENDAR_QUERY,
  UPDATE_ACTIVITY,
  USERS_QUERY,
} from '../lib/queries.js';
import { useSession } from '../lib/session.jsx';
import { useShortcut } from '../lib/shortcuts.jsx';
import { S } from '../lib/strings.js';

const VIEWS = [
  { key: 'week', label: S.calendar.week },
  { key: 'day', label: S.calendar.day },
  { key: 'agenda', label: S.calendar.agenda },
];

export function CalendarScreen() {
  const { isAdmin } = useSession();
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [view, setView] = useState('week');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // { activity } | { prefill }

  const days = useMemo(
    () => (view === 'day' ? [anchor] : persianWeekDays(anchor)),
    [anchor, view],
  );

  // The whole visible range, as instants. `calendar` returns everything that
  // OVERLAPS it, so an activity starting before the window still appears.
  const range = useMemo(() => {
    const from = new Date(days[0]);
    const to = new Date(days[days.length - 1]);
    to.setHours(23, 59, 59, 999);
    return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
  }, [days]);

  const { data, loading, error, refetch } = useQuery(CALENDAR_QUERY, {
    variables: { ...range, users: selectedUsers.length ? selectedUsers : null },
    fetchPolicy: 'cache-and-network',
  });
  const { data: typeData } = useQuery(ACTIVITY_TYPES_QUERY);
  const { data: userData } = useQuery(USERS_QUERY, {
    variables: { page: 1, pageSize: 100 },
    skip: !isAdmin,
  });

  const [updateActivity, updateState] = useMutation(UPDATE_ACTIVITY);

  const activities = data?.calendar ?? [];

  /**
   * A colour per activity type, by POSITION in the vocabulary.
   *
   * No code names a type — which types exist is deployment data. A slot list
   * means a deployment that renames its install type keeps its colour, and one
   * that adds a ninth wraps around rather than losing colour entirely.
   */
  const colorForType = useCallback(
    (typeId) => {
      const types = typeData?.activityTypes ?? [];
      const index = types.findIndex((type) => type.id === typeId);
      return activityPalette[(index < 0 ? 0 : index) % activityPalette.length];
    },
    [typeData],
  );

  const step = view === 'day' ? 1 : 7;
  const goToday = useCallback(() => setAnchor(startOfDay(new Date())), []);
  // Jumping to an arbitrary date. `[` and `]` step and `t` returns to today,
  // but «هفته اول مهر» was reachable only by pressing `]` five times — and a
  // congress three months out is not reachable at all in practice.
  const goToDate = useCallback((iso) => {
    const picked = isoToLocalDate(iso);
    if (picked) setAnchor(startOfDay(picked));
  }, []);
  const goPrev = useCallback(() => setAnchor((current) => addDays(current, -step)), [step]);
  const goNext = useCallback(() => setAnchor((current) => addDays(current, step)), [step]);
  const newActivity = useCallback(() => setDialog({ prefill: {} }), []);

  useShortcut('t', goToday, {
    description: S.shortcuts.actions.today,
    group: S.shortcuts.groups.calendar,
  });
  useShortcut('[', goPrev, {
    description: S.shortcuts.actions.prevWeek,
    group: S.shortcuts.groups.calendar,
  });
  useShortcut(']', goNext, {
    description: S.shortcuts.actions.nextWeek,
    group: S.shortcuts.groups.calendar,
  });
  useShortcut('n', newActivity, {
    description: S.shortcuts.actions.newActivity,
    group: S.shortcuts.groups.calendar,
  });

  /** Dragging an activity moves it, keeping its duration. */
  async function move(activity, deltaMinutes, day) {
    const start = new Date(activity.startsAt);
    const duration = activity.endsAt ? new Date(activity.endsAt) - start : 0;
    const moved = new Date(day);
    moved.setHours(start.getHours(), start.getMinutes() + deltaMinutes, 0, 0);
    await updateActivity({
      variables: {
        id: activity.id,
        input: {
          startsAt: moved.toISOString(),
          endsAt: duration ? new Date(moved.getTime() + duration).toISOString() : null,
        },
      },
    });
    refetch();
  }

  const title = view === 'day' ? formatDayHeader(anchor) : formatWeekRange(anchor);
  // The same range in Gregorian, beside the Jalali one rather than instead of
  // it. A single day needs one date; a week needs both ends, or «۲۹ Aug» would
  // describe only the Saturday.
  const gregorianTitle =
    view === 'day'
      ? formatGregorian(anchor)
      : `${formatGregorian(days[0], { withYear: false })} – ${formatGregorian(days[6])}`;

  return (
    <div className="flex h-screen flex-col">
      <header className="flex h-toolbar shrink-0 items-center gap-3 border-b border-border
                         bg-surface px-5">
        <CalendarDays size={18} className="text-primary" aria-hidden="true" />
        <h1 className="text-md font-bold">{S.calendar.title}</h1>

        <div className="flex items-center gap-1">
          {/* ChevronRight moves BACK: in RTL the visual "previous" is the arrow
              pointing right, and following the physical icon rather than the
              logical direction is how these end up reversed. */}
          <Button type="button" variant="ghost" onClick={goPrev} aria-label={S.shortcuts.actions.prevWeek}>
            <ChevronRight size={16} aria-hidden="true" />
          </Button>
          <Button type="button" onClick={goToday}>
            {S.calendar.today}
          </Button>
          <Button type="button" variant="ghost" onClick={goNext} aria-label={S.shortcuts.actions.nextWeek}>
            <ChevronLeft size={16} aria-hidden="true" />
          </Button>
        </div>

        {/* THE TITLE IS THE DATE PICKER. A calendar's range is the one thing on
            the toolbar that already names where you are, so hanging "go
            somewhere else" on it costs no width and is where people look. */}
        <JalaliDateButton
          value={toIsoDate(anchor)}
          onPick={goToDate}
          label={S.calendar.jumpToDate}
        >
          <span className="flex items-baseline gap-2">
            <span className="text-base font-medium">{title}</span>
            <span dir="ltr" className="text-xs text-text-muted">{gregorianTitle}</span>
          </span>
        </JalaliDateButton>

        <div className="ms-auto flex items-center gap-2">
          {isAdmin && (
            <select
              aria-label={S.calendar.whose}
              value={selectedUsers[0] ?? ''}
              onChange={(event) =>
                setSelectedUsers(event.target.value ? [event.target.value] : [])
              }
              className="h-control rounded border border-border bg-surface px-2 text-sm"
            >
              <option value="">{S.calendar.everyone}</option>
              {(userData?.users?.items ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          )}

          <div className="flex rounded border border-border">
            {VIEWS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => setView(entry.key)}
                className={[
                  'h-control px-3 text-sm',
                  view === entry.key
                    ? 'bg-primary text-primary-contrast'
                    : 'text-text hover:bg-surface-hover',
                ].join(' ')}
              >
                {entry.label}
              </button>
            ))}
          </div>

          <Button type="button" variant="primary" onClick={newActivity}>
            <Plus size={15} aria-hidden="true" />
            {S.calendar.newActivity}
          </Button>
        </div>
      </header>

      <MutationError error={error ?? updateState.error} />

      {loading && activities.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : view === 'agenda' ? (
        <AgendaList
          activities={activities}
          colorForType={colorForType}
          onOpen={(activity) => setDialog({ activity })}
        />
      ) : (
        <WeekGrid
          days={days}
          activities={activities}
          colorForType={colorForType}
          onCreate={(prefill) => setDialog({ prefill })}
          onOpen={(activity) => setDialog({ activity })}
          onMove={move}
        />
      )}

      <footer className="shrink-0 border-t border-border bg-surface px-5 py-2 text-xs text-text-muted">
        {S.calendar.dragHint}
      </footer>

      <ActivityDialog
        open={Boolean(dialog)}
        onOpenChange={(open) => !open && setDialog(null)}
        activity={dialog?.activity}
        prefill={dialog?.prefill}
        onSaved={() => refetch()}
      />
    </div>
  );
}

/** The same activities, read as a list. Grouped by day, newest first within it. */
function AgendaList({ activities, colorForType, onOpen }) {
  const grouped = useMemo(() => {
    const map = new Map();
    for (const activity of [...activities].sort(
      (a, b) => new Date(a.startsAt) - new Date(b.startsAt),
    )) {
      const key = new Date(activity.startsAt).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(activity);
    }
    return [...map.entries()];
  }, [activities]);

  if (grouped.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-text-muted">
        {S.calendar.noActivities}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="flex flex-col gap-5">
        {grouped.map(([key, dayActivities]) => (
          <section key={key} className="flex flex-col gap-2">
            <h2 className="flex items-baseline gap-2 text-sm font-medium text-text-muted">
              {formatDayHeader(new Date(key))}
              <span dir="ltr" className="text-xs">
                {formatGregorian(new Date(key), { withYear: false })}
              </span>
            </h2>
            {dayActivities.map((activity) => (
              <div key={activity.id} className="h-row">
                <ActivityChip
                  activity={activity}
                  color={colorForType(activity.type?.id)}
                  onClick={() => onOpen(activity)}
                />
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
